import React from 'react';
import { useRef, useCallback, useContext, useEffect, useState } from "react";
import { BrowserRouter, Route, Switch, Redirect } from "react-router-dom";
import { useSelector } from 'react-redux';
import { UserContext } from "./context/UserContext";
import { getURI } from "./api/apiConfig";
import Loader from "./Loader";
import AdminLayout from "layouts/Admin.js";
import AuthLayout from "layouts/Auth.js";
import {
  selectImpersonationMode,
  selectSessionStatus,
  selectImpersonationStatus,
} from './features/session/sessionSlice';
import { selectNtAdministrator } from './features/user/userSlice';
import { log, useLog } from "./Log.init";
import { RouterPrompt } from "@features/ux/RouterPrompt";
import { selectViewIsDirty } from "@features/ux/uxSlice";
import { useLogout } from "@src/api/hooks/Logout";
import { generateCorrelationId } from '@src/helpers/generateCorrelationId';
import SupportedLanguagesMigrationModal from 'components/Tenant/SupportedLanguagesMigrationModal';
import {
  useCheckSupportedLanguagesMigrationQuery,
  useExecuteSupportedLanguagesMigrationMutation,
} from 'api/tenant.api';
import { toast } from 'react-toastify';

class DebugRouter extends BrowserRouter {
  constructor(props) {
    super(props);
    log.info({ message: `initial history is: ${JSON.stringify(this.history, null, 2)}` })
    this.history.listen((location, action) => {
      log.info({ message: `The current URL is ${location.pathname}${location.search}${location.hash}` })
      log.info({
        message: `The last navigation action was ${action}, ${JSON.stringify(this.history, null, 2)});`,
      });
    });
  }
}

function App() {
  const [userContext, setUserContext] = useContext(UserContext);
  const impersonationMode = useSelector(selectImpersonationMode);
  const sessionStatus = useSelector(selectSessionStatus);
  const impersonationStatus = useSelector(selectImpersonationStatus);
  const ntAdministrator = useSelector(selectNtAdministrator);
  const viewIsDirty = useSelector(selectViewIsDirty);
  const { fetchLogout } = useLogout();

  const [showMigrationModal, setShowMigrationModal] = useState(false);
  const [migrationLanguages, setMigrationLanguages] = useState([]);
  const [hasCheckedMigration, setHasCheckedMigration] = useState(false);

  const isSessionReady = ntAdministrator
    ? (impersonationMode && impersonationStatus === 'active')
    : sessionStatus === 'active';

  const { data: migrationStatus, error: migrationError, isLoading: isCheckingMigration } = useCheckSupportedLanguagesMigrationQuery(undefined, {
    skip: !userContext.token || hasCheckedMigration || !isSessionReady,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });
  
  const [executeMigration, { isLoading: isMigrating }] =
    useExecuteSupportedLanguagesMigrationMutation();

  useEffect(() => {
    setHasCheckedMigration(false);
    setShowMigrationModal(false);
  }, [impersonationMode]);

  useEffect(() => {
    if (migrationStatus !== undefined) {
      setHasCheckedMigration(true);
      if (migrationStatus.needsMigration) {
        setMigrationLanguages(migrationStatus.parameterSetLanguages || []);
        setShowMigrationModal(true);
      }
    } else if (migrationError) {
      setHasCheckedMigration(true);
    }
  }, [migrationStatus, migrationError]);
  
  
  const handleMigrate = async () => {
    try {
      const result = await executeMigration().unwrap();
      
      if (result.success && result.migrated) {
        toast.success('Supported languages migrated successfully!', {
          position: toast.POSITION.BOTTOM_CENTER,
          autoClose: 3000,
        });
        setShowMigrationModal(false);
      } else {
        toast.info(result.message || 'No migration needed', {
          position: toast.POSITION.BOTTOM_CENTER,
          autoClose: 3000,
        });
        setShowMigrationModal(false);
      }
    } catch (error) {
      toast.error('Failed to migrate supported languages. Please try again later.', {
        position: toast.POSITION.BOTTOM_CENTER,
        autoClose: 5000,
      });
      setShowMigrationModal(false);
    }
  };

  const verifyUser = () => {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json')
    headers.append('X-CORRELATION-ID', generateCorrelationId());
    fetch(getURI("refreshtoken"), {   // change this to fetchwithtimeout
      method: "POST",
      credentials: "include",
      headers,
      body: JSON.stringify({ impersonation: impersonationMode })
    }).then(async (response) => {
      if (response.ok) {
        const data = await response.json();
        setUserContext((oldValues) => {
          return { ...oldValues, token: data.token, impersonation: data.impersonation };
        });
      } else {
        setUserContext((oldValues) => {
          return { ...oldValues, token: null, impersonation: false };
        });
      }
    });
  }

  let interval = useRef(null);

  useEffect(() => {
    if (interval.current) clearInterval(interval.current);
    
    if (userContext.token) {
      interval.current = setInterval(() => {
        verifyUser();
      }, 5 * 60 * 1000);
    }

    return () => clearInterval(interval.current);
  }, [userContext.token]);

  useEffect(() => { verifyUser() }, [impersonationMode]);

  useEffect(() => {
    window.addEventListener('logoutHandler', (e) => {
      fetchLogout();
      clearInterval(interval.current);
    })
  }, []);

  const syncLogout = useCallback((event) => {
    if (event.key === "logout") {
      window.location.reload();
    }
  }, []);

  useEffect(() => {
    window.addEventListener("storage", syncLogout);
    return () => {
      window.removeEventListener("storage", syncLogout);
    };
  }, [syncLogout]);

  useLog().singleton()


  return userContext.token === null ? (
    <BrowserRouter
      getUserConfirmation={() => {
        /* Empty callback to block the default browser prompt */
      }}
    >
      <Switch>
        <Route path="/auth" component={AuthLayout} />
        <Route path="/auth/login" component={AuthLayout} />
        <Route path="/auth/register" component={AuthLayout} />
        <Redirect from="/" to="/auth/login" />
      </Switch>
    </BrowserRouter>
  ) : userContext.token ? (
    <BrowserRouter>
      <RouterPrompt
        when={viewIsDirty}
      />
      <Switch>
        <Route path="/admin" component={AdminLayout} />
        <Redirect from="/" to="/admin/dashboard" />
      </Switch>
      <SupportedLanguagesMigrationModal
        open={showMigrationModal}
        onMigrate={handleMigrate}
        languages={migrationLanguages}
        loading={isMigrating}
      />
    </BrowserRouter>
  ) : (
    <Loader />
  );
}

export default App;